import { defineStore } from 'pinia'
import type { User } from '@/types/user'
import { authService, type OnboardingInput, type RegisterInput } from '@/services/auth.service'
import { getAuthToken, setAuthToken } from '@/services/http/httpClient'

const STORAGE_KEY = 'ereach.auth.user'

/** "Ține-mă minte" decides whether the session survives closing the browser — token included. */
function moveAuthToken(rememberMe: boolean): void {
  const token = getAuthToken()
  if (token) setAuthToken(token, rememberMe)
}

function readPersistedUser(): User | null {
  const raw = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<User>
    // Sessions persisted before onboarding existed have no flag — treat them as done, not as brand-new companies.
    return { ...parsed, onboardingCompleted: parsed.onboardingCompleted ?? true } as User
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: readPersistedUser(),
    status: 'idle' as 'idle' | 'loading' | 'error',
    /** Set between "password accepted" and "code accepted"; never persisted. */
    twoFactorChallenge: null as string | null,
  }),
  getters: {
    isAuthenticated: (state) => state.user !== null,
  },
  actions: {
    /**
     * "Remember me" picks the storage, not just whether we remember: localStorage
     * survives closing the browser, sessionStorage clears with the tab — a real
     * difference for a shared shop/reception device, not a decorative checkbox.
     */
    /**
     * Returns 'two-factor' when the password was right but a code is still
     * needed; the caller then collects it and calls completeTwoFactor().
     */
    async login(email: string, password: string, rememberMe: boolean): Promise<'done' | 'two-factor'> {
      this.status = 'loading'
      try {
        const result = await authService.login(email, password)
        if ('twoFactorChallenge' in result) {
          this.twoFactorChallenge = result.twoFactorChallenge
          this.status = 'idle'
          return 'two-factor'
        }
        this.establish(result.user, rememberMe)
        this.status = 'idle'
        return 'done'
      } catch (error) {
        this.status = 'error'
        throw error
      }
    },

    async completeTwoFactor(code: string, rememberMe: boolean) {
      if (!this.twoFactorChallenge) throw { code: 'token_expired', message: 'Sesiunea de verificare a expirat.' }
      this.status = 'loading'
      try {
        const user = await authService.completeTwoFactor(this.twoFactorChallenge, code)
        this.twoFactorChallenge = null
        this.establish(user, rememberMe)
        this.status = 'idle'
      } catch (error) {
        this.status = 'error'
        throw error
      }
    },

    cancelTwoFactor() {
      this.twoFactorChallenge = null
    },

    establish(user: User, rememberMe: boolean) {
      this.user = user
      const storage = rememberMe ? localStorage : sessionStorage
      storage.setItem(STORAGE_KEY, JSON.stringify(user))
      // The API token must live in the same storage as the session it belongs to.
      moveAuthToken(rememberMe)
    },
    /** Sign-up logs the new owner straight in (remembered) so onboarding can start without a second form. */
    async register(input: RegisterInput) {
      this.status = 'loading'
      try {
        const user = await authService.register(input)
        this.establish(user, true)
        this.status = 'idle'
      } catch (error) {
        this.status = 'error'
        throw error
      }
    },
    async completeOnboarding(input: OnboardingInput) {
      const user = await authService.completeOnboarding(input)
      this.setUser(user)
    },
    /** Writes back to whichever storage currently holds the session, so "remember me" is preserved. */
    setUser(user: User) {
      this.user = user
      const storage = localStorage.getItem(STORAGE_KEY) ? localStorage : sessionStorage
      storage.setItem(STORAGE_KEY, JSON.stringify(user))
    },
    /** Drops the local session without calling the API — used when the API already rejected us. */
    forgetSession() {
      this.user = null
      this.twoFactorChallenge = null
      localStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(STORAGE_KEY)
      setAuthToken(null)
    },
    async logout() {
      try {
        await authService.logout()
      } finally {
        // The local session goes even if revoking the token server-side failed —
        // otherwise a network blip would leave someone logged in on a shared device.
        this.forgetSession()
      }
    },
  },
})
